import { formatMoney } from '@/utils/currency';

function escapeHtml(s: string) {
  if (!s) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function downloadQuotationPdf(quotationDetails: any, profile: any) {
  // Dynamically import heavy PDF generation libraries
  const [html2canvas, jsPDF] = await Promise.all([
    import('html2canvas').then((m) => m.default),
    import('jspdf').then((m) => m.default),
  ]);

  const companyName = profile?.company_name || profile?.company?.name || profile?.company || 'Company Name';
  const companyAddress = profile?.address || '';
  const customerName = quotationDetails?.customer_name || quotationDetails?.customer || 'Customer';
  const customerContact = quotationDetails?.contact_display || '';
  const quotationDate = new Date(quotationDetails?.creation || new Date()).toLocaleDateString();
  const validUntil = quotationDetails?.valid_till ? new Date(quotationDetails.valid_till).toLocaleDateString() : '';

  const logoHtml = profile?.company_logo ? `<img src="${escapeHtml(profile.company_logo)}" style="max-height: 60px; margin-bottom: 10px;" />` : '';
  const companyPhone = profile?.phone || profile?.mobile || profile?.contact_no || '';
  const preparer = quotationDetails?.owner || profile?.full_name || 'Staff';
  
  const footerText = typeof window !== 'undefined' && window.location.hostname.includes('kashly') ? 'Powered by Kashly' : 'Powered by Saledesk';

  const htmlContent = `
    <div id="quotation-pdf-root" style="width:794px;padding:40px;background:#fff;color:#333;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;box-sizing:border-box;">
      
      <!-- Header Section -->
      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:30px;">
        <div style="width:50%;">
          ${logoHtml}
          <div style="font-size:20px; font-weight:700; color:#111; margin-bottom:4px;">${escapeHtml(companyName)}</div>
          <div style="font-size:11px; color:#555; line-height:1.4;">
            ${companyAddress ? escapeHtml(companyAddress) + '<br>' : ''}
            ${companyPhone ? escapeHtml(companyPhone) : ''}
          </div>
        </div>
        <div style="width:50%; display:flex; flex-direction:column; align-items:flex-end;">
          <div style="font-size:28px; font-weight:300; color:#111; letter-spacing:1px; margin-bottom:12px;">QUOTATION</div>
          <table style="width:auto; font-size:11px; text-align:right;">
            <tr>
              <td style="color:#666; padding-bottom:4px; padding-right:24px;">Date</td>
              <td style="font-weight:600; color:#111; padding-bottom:4px;">${escapeHtml(quotationDate)}</td>
            </tr>
            <tr>
              <td style="color:#666; padding-bottom:4px; padding-right:24px;">Valid Until</td>
              <td style="font-weight:600; color:#111; padding-bottom:4px;">${escapeHtml(validUntil)}</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Customer Section -->
      <div style="margin-bottom:30px; padding-top:20px; border-top:2px solid #f3f4f6;">
        <div style="font-size:10px; font-weight:700; color:#888; text-transform:uppercase; margin-bottom:6px;">Quotation For</div>
        <div style="font-size:14px; font-weight:600; color:#111; margin-bottom:4px;">${escapeHtml(customerName)}</div>
        <div style="font-size:11px; color:#555; line-height:1.4;">
          ${customerContact ? escapeHtml(customerContact) : ''}
        </div>
      </div>

      <!-- Items Table -->
      <table style="width:100%; border-collapse:collapse; font-size:11px; margin-bottom:20px;">
        <thead>
          <tr style="border-bottom:2px solid #e5e7eb;">
            <th style="text-align:left; padding:8px 4px; color:#444; font-weight:600; width:55%;">DESCRIPTION</th>
            <th style="text-align:center; padding:8px 4px; color:#444; font-weight:600; width:15%;">QTY</th>
            <th style="text-align:right; padding:8px 4px; color:#444; font-weight:600; width:15%;">PRICE</th>
            <th style="text-align:right; padding:8px 4px; color:#444; font-weight:600; width:15%;">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          ${(quotationDetails?.items || []).map((item: any) => `
            <tr style="border-bottom:1px solid #f3f4f6;">
              <td style="padding:10px 4px; color:#111;">${escapeHtml(item.item_name)}</td>
              <td style="padding:10px 4px; text-align:center; color:#333;">${escapeHtml(item.qty || item.quantity || 0)}</td>
              <td style="padding:10px 4px; text-align:right; color:#333;">${escapeHtml(formatMoney(item.rate || 0))}</td>
              <td style="padding:10px 4px; text-align:right; color:#111; font-weight:500;">${escapeHtml(formatMoney(item.amount || 0))}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Totals Section -->
      <div style="display:flex; justify-content:flex-end;">
        <table style="width:300px; font-size:11px; border-collapse:collapse;">
          <tr>
            <td style="padding:6px 0; color:#555;">Subtotal</td>
            <td style="padding:6px 0; text-align:right; font-weight:500; color:#111;">${escapeHtml(formatMoney(quotationDetails?.net_total || 0))}</td>
          </tr>
          <tr>
            <td style="padding:6px 0; color:#555; border-bottom:1px solid #f3f4f6;">Sales Tax</td>
            <td style="padding:6px 0; text-align:right; font-weight:500; color:#111; border-bottom:1px solid #f3f4f6;">${escapeHtml(formatMoney(quotationDetails?.total_taxes_and_charges || 0))}</td>
          </tr>
          <tr>
            <td style="padding:12px 0; color:#111; font-weight:700; font-size:14px;">Total</td>
            <td style="padding:12px 0; text-align:right; font-weight:700; font-size:14px; color:#111;">${escapeHtml(formatMoney(quotationDetails?.grand_total || quotationDetails?.net_total || 0))}</td>
          </tr>
        </table>
      </div>

      <!-- Footer -->
      <div style="margin-top:50px; padding-top:15px; border-top:1px solid #e5e7eb; display:flex; justify-content:space-between; align-items:center;">
        <div style="font-size:10px; color:#888;">
          Prepared by: <span style="font-weight:600; color:#555;">${escapeHtml(preparer)}</span>
        </div>
        <div style="font-size:10px; color:#aaa;">
          ${escapeHtml(footerText)}
        </div>
      </div>

    </div>
  `;

  // Create temporary container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '794px';
  container.style.zIndex = '-9999';
  container.style.opacity = '0';
  container.style.pointerEvents = 'none';
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    const root = container.querySelector('#quotation-pdf-root') as HTMLElement;
    const canvas = await html2canvas(root, { 
      scale: 2, 
      useCORS: true,
      width: 794,
      windowWidth: 794
    });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Quotation_${quotationDetails?.name || 'Download'}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
